/**
 * The console side of the staff presence beacon (task 35). services/ops's
 * `GET /ops/presence` can only ever report real typing/reading activity if
 * something on this side actually calls `ops.presenceBeacon` — this file is
 * that "something," split into two independently testable pieces:
 *
 *  - `createTypingReporter` — turns "every keystroke in the composer" into
 *    a throttled+debounced pair of network calls, never one call per key.
 *  - the plain focus report itself is just `ops.presenceBeacon(threadId,
 *    false)` — ThreadPane calls it directly (on selection, and on a
 *    heartbeat interval while a thread stays selected) rather than through
 *    a wrapper here, since there is no per-keystroke flood to tame on that
 *    path — but its interval and TTL-relationship are documented here
 *    alongside the typing one so the two read as one coherent policy.
 *
 * Both intervals below are deliberately smaller than their corresponding
 * server-side TTL (services/ops/src/presence.ts) by a healthy margin, so a
 * single dropped request — Andrew's own standing note is "assume the
 * connection blips, design for it" — never flips a badge off and straight
 * back on:
 *   - typing: reported at most once every `TYPING_THROTTLE_MS` (2.5s) while
 *     actively typing; the server's typing TTL is 6s (~2.4x), so one missed
 *     report is absorbed silently.
 *   - focus/reading: ThreadPane re-reports on a `FOCUS_HEARTBEAT_MS` (15s)
 *     heartbeat while a thread stays selected; the server's reading TTL is
 *     45s (3x), so two consecutive missed heartbeats still don't clear the
 *     outline badge.
 */
export const TYPING_THROTTLE_MS = 2_500
export const TYPING_DEBOUNCE_MS = 2_500
/** How often ThreadPane re-reports focus on the currently selected thread. */
export const FOCUS_HEARTBEAT_MS = 15_000

export interface TypingReporterOptions {
  /** Minimum gap between "typing: true" reports while keystrokes keep coming. */
  throttleMs?: number
  /** Quiet period after the last keystroke before a "typing: false" report goes out. */
  debounceMs?: number
  /** Overridable for tests — must return the same unit `Date.now()` does (ms). */
  now?: () => number
  setTimeoutImpl?: typeof setTimeout
  clearTimeoutImpl?: typeof clearTimeout
}

export interface TypingReporter {
  /** Call on every composer keystroke (i.e. every `onChange`). Safe to call as often as the caller likes. */
  notifyKeystroke(): void
  /** Cancels any pending debounced "stopped typing" report without sending one — call on unmount or when switching threads, where the focus-change report already implies "not typing here anymore." */
  dispose(): void
}

/**
 * Wraps a raw `report(typing)` callback — in practice always
 * `(typing) => { void ops.presenceBeacon(threadId, typing).catch(() => {}) }`
 * — so a composer's `onChange` can call `notifyKeystroke()` on every single
 * keystroke without that becoming a request per keystroke (task 35's
 * explicit requirement). Two independent techniques doing two different
 * jobs:
 *
 *  - THROTTLE: while keys keep coming, "typing: true" goes out at most once
 *    per `throttleMs` — the first keystroke after being idle reports
 *    immediately (so the badge lights up right away), and further
 *    keystrokes inside the same window are silent.
 *  - DEBOUNCE: after `debounceMs` of no further keystrokes, exactly one
 *    "typing: false" report goes out — a proactive "I stopped," sent
 *    without waiting for the server's own typing TTL to lapse, so the
 *    filled badge clears within one debounce window of a normal pause in
 *    typing rather than only when the TTL happens to expire.
 */
export function createTypingReporter(report: (typing: boolean) => void, opts: TypingReporterOptions = {}): TypingReporter {
  const throttleMs = opts.throttleMs ?? TYPING_THROTTLE_MS
  const debounceMs = opts.debounceMs ?? TYPING_DEBOUNCE_MS
  const now = opts.now ?? Date.now
  const setTimeoutImpl = opts.setTimeoutImpl ?? setTimeout
  const clearTimeoutImpl = opts.clearTimeoutImpl ?? clearTimeout

  let lastSentAt = -Infinity
  let debounceHandle: ReturnType<typeof setTimeout> | null = null

  function clearDebounce(): void {
    if (debounceHandle !== null) {
      clearTimeoutImpl(debounceHandle)
      debounceHandle = null
    }
  }

  function notifyKeystroke(): void {
    const t = now()
    if (t - lastSentAt >= throttleMs) {
      lastSentAt = t
      report(true)
    }
    clearDebounce()
    debounceHandle = setTimeoutImpl(() => {
      debounceHandle = null
      lastSentAt = -Infinity
      report(false)
    }, debounceMs)
  }

  function dispose(): void {
    clearDebounce()
  }

  return { notifyKeystroke, dispose }
}
